'use client';

import * as React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import SquaredDotIcon from './Icons/SquaredDotIcon';
import Button from './Button';
import { useTranslations } from 'next-intl';
import { storeFront } from '@/lib/utils/routeLinks';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

interface TagsComboboxProps {
  categories: Record<string, any>[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSelect?: (categoryId: string) => void;
}

export default function TagsCombobox({
  categories,
  searchQuery,
  onSearchChange,
  onSelect,
}: TagsComboboxProps) {
  const searchParams = useSearchParams();
  const tagIds = searchParams.get('tag_ids');
  const t = useTranslations();
  const [open, setOpen] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState('');
  const selectedItem = React.useRef<Record<string, any> | null>(null);
  const [prevCategories, setPrevCategories] = React.useState<
    Record<string, any>[]
  >(categories?.length ? categories : []);

  const handleSelect = (value: string) => {
    setPrevCategories(categories);
    setSelectedValue(value);
    onSelect?.(value);
    setOpen(false);
  };

  let selectedLabel: string | null = null;

  if (selectedItem?.current) {
    selectedLabel = selectedItem.current?.label ?? null;
  } else {
    const queryCategory = prevCategories.find((cat) => cat.value === tagIds);
    if (queryCategory) {
      selectedLabel = queryCategory.label;
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          transparent
          role="combobox"
          aria-expanded={open}
          className="flex w-full min-w-[120px] items-center justify-between space-x-2 px-3 py-2 text-left lg:justify-start"
        >
          <div className="flex items-center space-x-2">
            <span>
              <SquaredDotIcon />
            </span>
            <span>{selectedLabel ?? t('common.category')}</span>
          </div>
          <div className="text-primary-800">
            {open ? <ChevronUp /> : <ChevronDown />}
          </div>
        </Button>
      </PopoverTrigger>

      {/* <PopoverContent className="w-80 bg-white p-0" align="start"> */}
      <PopoverContent
        className="w-[250px] rounded-md bg-white px-3 py-3"
        align="start"
      >
        <Command className="rounded-lg border-0" shouldFilter={false}>
          <CommandInput
            onValueChange={onSearchChange}
            placeholder={t('aria.search')}
            className="h-8"
            value={searchQuery}
            onClear={
              searchQuery?.length > 0
                ? () => {
                    // setSelectedValue('');
                    onSearchChange('');
                  }
                : undefined
            }
          />
          <CommandList>
            <CommandEmpty>{t('common.nothing_found')}</CommandEmpty>
            <CommandGroup className="p-0">
              {categories.map((category) => (
                <CommandItem
                  key={category.value}
                  value={category.value}
                  onSelect={handleSelect}
                  className={cn(
                    'mx-2 my-1 cursor-pointer rounded-lg py-2 hover:bg-primary-800 hover:text-white',
                    selectedValue === category.id && 'bg-primary-800 text-white'
                  )}
                >
                  {category.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>

        {/* Footer link */}
        <div className="px-4 py-3">
          <Link
            href={storeFront.onlineShops.de()}
            className="text-sm font-medium text-rose-600 underline transition-colors hover:text-rose-700"
          >
            {t(`common.other_categories`)}
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
